import * as THREE from 'three';
import { /* percentage ,*/ resetImagePositions } from '../Utils/GlobalFunctions';
import { addFilmStrip } from '../Utils/filmStrip';
import { ringBirth } from '../Utils/ringGroup';
import { categoryFor, galleryBase, loadArt } from '../Utils/artSource';

// The gallery's own art, grouped by category, or null until it has been fetched.
//
// It used to be `require.context` over Assets/Images/Categories/*: the art was whatever was
// committed to this repo at build time, so a new upload to the gallery could not appear here
// without a redeploy. It now comes from the gallery's API — see Utils/artSource.js for the
// three things that have to be right about that — which is the point of the two projects
// sharing a viewer and an admin panel at all.
let ART = null;
let artRequest = null;

// Rings that were asked for before the art arrived. The planets are built as soon as the
// scene loads, and that must not wait on a network request, so a planet that wants a ring in
// the meantime joins this list and gets it when the fetch lands.
const awaitingArt = [];

/** The art, or null if it has not arrived yet. */
export function GetAll() {
    return ART;
}

/**
 * Fetches the art once for the whole app and returns the request.
 *
 * Called at startup (index.js) and by anything that mounts later and finds nothing yet, so
 * the request is held rather than repeated: one page, one fetch of the gallery.
 */
export function SetAll() {
    if (!artRequest) {
        // The gallery is the parent folder of wherever this page is served from, worked out
        // from the document rather than assumed — see Utils/artSource.js.
        artRequest = loadArt({ base: galleryBase(document.baseURI) })
            .then(art => {
                ART = art;
                awaitingArt.splice(0).forEach(set);
                return art;
            })
            .catch(error => {
                // The planets, the lobby and the sun are the page; the art is what hangs on
                // them. A gallery that cannot be read is worth reporting and carrying on
                // without, rather than refusing to draw the scene.
                console.error('AddImages: the gallery API could not be read — the planets will have no artwork.', error);
                return null;
            });
    }

    return artRequest;
}

export function setPlanet(planet) {

    planet.traverse(child => {
        //get the label mesh to create new img group
        if (child.isMesh && child.geometry.type === 'TextGeometry') {
            const imgGroup = new THREE.Group();
            imgGroup.userData.isImgGroup = true;
            planet.userData.labelName = child.name;
            // Named after the label the planet is drawn with, because MainScene looks the
            // group up by `planet.userData.imgGroupLabel`, which is that same text. This used
            // to be `child.name.split('_')[0]`, which is why Jupiter — labelled 'Web3D_VR' —
            // asked for a group called 'Web3D' and never showed a single image.
            imgGroup.name = planet.userData.imgGroupLabel;
            imgGroup.position.y -= planet.userData.radius / 4;
            imgGroup.rotation.set(0, 0, 0);
            planet.add(imgGroup);

            if (ART) set(imgGroup);
            else awaitingArt.push(imgGroup);
        }
    });
}

function set(imgGroup) {
    const category = categoryFor(imgGroup.parent.name);
    const records = category ? ART[category] : null;

    if (!records || !records.length) return;

    // Every work the category holds, clips included. A clip used to be dropped here, on the
    // grounds that this loader reads images and a clip's thumbnail is a VideoTexture rather than
    // what `TextureLoader` hands back — but that same filtered count went on to size the film
    // strip, so a category holding nothing but clips came out with no card, no strip, and no frame
    // for a card to stand in either. That is Jupiter: `PLANET_CATEGORY` (Utils/artSource) sends it
    // the VR category, the gallery's VR category holds one work, and that work is the clip.
    const works = records;

    // The strip first, and before a single texture is asked for: it is built from how many works
    // the ring is for rather than from the cards, so there is nothing for it to wait on, and the
    // ring is framed before the artwork it holds arrives instead of seconds behind it. The count
    // is left on the group as well, because the cards' own placement reads it — a texture that
    // never arrives has to leave its slot empty rather than pull every card after it off the
    // window drawn for it.
    imgGroup.userData.cardCount = works.length;
    addFilmStrip(imgGroup, works.length);

    const manager = new THREE.LoadingManager();
    const textureLoader = new THREE.TextureLoader(manager);

    manager.onError = function (url) {
        // Naming the URL, because a ring that is short of one image looks the same as art
        // that was never uploaded — and on a fresh install the likeliest cause is a path.
        console.error('AddImages: could not load artwork', url);
    };

    works.forEach((record, slot) => {
        // A clip is not loaded, it is played: `VideoTexture` re-uploads the element every frame, so
        // the card is the clip itself rather than a still taken from it. It arrives on the same
        // path as an image — the promise resolves into `create`, which hangs it on its ring — and
        // that is what keeps the ring's placement waiting for a clip the way `decode()` waits for
        // an image whose pixels are not in the cache yet.
        if (record.mediaType === 'video') {
            videoTexture(record).then(texture => create(texture, imgGroup, record, slot));
            return;
        }

        // The gallery's downscaled copy when it has one, and the original when it does not. This
        // is the only texture the ring ever holds — a card is drawn about 200 px wide — and the
        // original can be 17 MB and 42 MP, which the cap below then throws away for a second time
        // after the bytes have already crossed the wire. Twenty seven of those at startup, all at
        // once, is the ring arriving one image at a time. The fallback is not a repair: a work
        // whose thumb could not be built (a host without GD) is loaded exactly as it was before.
        textureLoader.load(record.thumbUrl || record.url, texture => {
            // The bytes arriving and the header parsing is what `onload` means — not that the
            // pixels can be drawn yet. `create` copies the source into a canvas at this instant
            // (see `fitToGpu`), and `drawImage` of an image whose raster is not ready draws
            // *nothing*, and says nothing: the canvas stays transparent, a transparent canvas is
            // perfectly valid upload data, and the card is left RGBA(0,0,0,0) — drawn, raycastable,
            // invisible. That is the artwork that opens its lightbox out of empty space.
            //
            // Measured on a cold load (tools/headless/probe-card-copies.js): 8 of 24 copies came
            // back empty, with no GL error raised for any of them — which is why counting GL
            // errors reported this ring clean, and why the cap and the copy both looked like fixes.
            // The same source size lands on both sides of the split, so it is the moment the copy
            // is taken rather than the size of the file.
            //
            // `decode()` is the one API that promises the raster is there, and it re-decodes from
            // the source when the browser has since reclaimed the pixels. Measured, it took the
            // empty copies from 8 in 24 to 0-1: the one that survived is a rejected decode, and a
            // rejection is not worth losing the card over, so it copies what it would have copied
            // without this. It is worth *saying*, though — that is the whole reason this went
            // unnoticed across two rounds of fixing the wrong thing.
            const decoded = texture.image && texture.image.decode
                ? texture.image.decode().catch(error => {
                    console.warn('AddImages: could not decode before cutting the copy, so this card may be blank —', record.url, error);
                })
                : Promise.resolve();

            decoded.then(() => create(texture, imgGroup, record, slot));
        });
    });
}

// The GPU copy is capped, and it is always a copy — the two halves matter for different reasons.
//
// The cap is about the browser's budget. A card is drawn about 200 px wide in its window on the
// ring, and this gallery's source files run to 8075x5225 — 42 megapixels, ~170 MB of texture memory
// for one frame of a film strip. Twenty seven of those is 472 Mpx, ~1.8 GB of texture (2.4 GB with
// mipmaps), asked of the browser at startup, every ring at once. It refuses the tail of that: 12 of
// the 27 uploads came back `WebGL: INVALID_VALUE: texSubImage2D: bad image data`, and a *different*
// 12 on the next run, and none of them when re-uploaded in isolation. three does not read GL errors
// and so never retries, and `texStorage2D` zero-fills immutable storage — so a refused upload leaves
// the card RGBA(0,0,0,0): drawn, raycastable, invisible. That is the artwork that opens its lightbox
// out of empty space, which is how this was reported. Capping the long side at 2048 takes the ring
// from 472 Mpx to 86 Mpx / 330 MB, and the failures from 12 to 1.
//
// The copy is about *whose raster it is*. The survivor of that first fix was the ring's last
// DOM-element upload — 1920x1080, under the cap and so untouched — and it failed on both cold runs,
// on a plain 8-bit RGBA PNG with no APNG in it, while 23 canvas copies of far larger files went
// clean. An `HTMLImageElement` upload reads the browser's decoded-image cache, which under a cold
// load of this size can already have been purged; that path cannot re-decode, so it errors.
// `drawImage` into a canvas can re-decode, so once the raster is there the copy is real and the
// upload has nothing left to fail on. It does not *wait* for it, though — which is the second half
// of this story and the reason for the `decode()` on the load path above: a copy taken of an image
// whose raster is not ready yet is nothing at all, silently, and a transparent canvas is valid
// upload data that raises no GL error (8 of 24 copies, measured). So the copy is unconditional, the
// decode gate is what makes it real, and the cap only decides its size.
//
// Measured with tools/headless/probe-texsub.js against this page. The probe keeps a fresh profile
// per run deliberately: a warm load always came back clean.
const CARD_TEXTURE_MAX = 2048;

/**
 * The image to hand the GPU: a canvas copy of the artwork, `maxSide` at most on its long side.
 *
 * Aspect is kept, so the geometry read off it below is unchanged and the window cut for it goes on
 * agreeing with it. jsdom hands out no 2D context, and this is what the test suite runs `create`
 * under: no context, no copy, the original image back.
 */
function fitToGpu(image, maxSide) {
    const width = image.naturalWidth || image.width;
    const height = image.naturalHeight || image.height;

    if (!(width > 0) || !(height > 0)) return image;

    const scale = Math.min(1, maxSide / Math.max(width, height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));

    const ctx = canvas.getContext('2d');
    if (!ctx) return image;

    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

    return canvas;
}

/**
 * The texture for a clip: a `VideoTexture` over a muted, looping `<video>`, resolved once the
 * element's metadata is in.
 *
 * Muted and looping is what a page of thumbnails wants, and what the gallery's own grid does with a
 * clip — it plays in place, and clicking it opens the lightbox, which plays it full. `playsInline`
 * is the third part of that: without it iOS takes the clip full-screen on its own, which is the
 * lightbox's job rather than the ring's.
 *
 * The wait for metadata is not politeness. `create` cuts the card's geometry from the texture's own
 * dimensions, and a `<video>` reports none until it has them — `videoWidth` is 0 before that, and a
 * geometry built on `0/0` is a card that draws nothing while still answering a click. On the
 * element the dimensions are `videoWidth`/`videoHeight`; `video.width` is the *attribute*, which
 * nothing sets.
 *
 * A clip that fails to load resolves nothing, so its frame stays empty — which is what an image that
 * 404s does. Being refused permission to start is a different case and not a broken card: the clip
 * is then a still, which is worth saying and not worth losing the card over.
 */
function videoTexture(record) {
    const video = document.createElement('video');
    video.src = record.url;
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('muted', '');

    const texture = new THREE.VideoTexture(video);

    return new Promise(resolve => {
        video.addEventListener('error', () => {
            console.error('AddImages: could not load artwork', record.url);
        }, { once: true });

        video.addEventListener('loadedmetadata', () => {
            const played = video.play();

            if (played && played.catch) {
                played.catch(error => {
                    console.warn('AddImages: the clip could not be started on its card, so it stands as a still —', record.url, error);
                });
            }

            resolve(texture);
        }, { once: true });
    });
}

// Exported for its own test, as Utils/filmStrip's pure helpers are: `create` is where a card
// joins its ring, and the moment it does is the whole of the ordering bug below.
export function create(texture, imgGroup, record, slot) {
    const radius = imgGroup.parent.userData.radius;
    const height = radius * 0.75;
    // A clip is exempt from the cap, and not because of its size: `fitToGpu` draws the source into a
    // canvas, which would take one frame of a running clip and hand back a still — and the two
    // dimensions below are on `videoWidth`/`videoHeight`, which that copy does not read. The
    // browser budget the cap exists for is not in question either: a ring holds at most one clip.
    const clip = texture.isVideoTexture === true;
    const source = texture.image;

    if (!clip) {
        // Sized before anything reads it, because the geometry below is taken off it.
        texture.image = fitToGpu(texture.image, CARD_TEXTURE_MAX);
    }

    const aspectRatio = clip
        ? source.videoWidth / source.videoHeight
        : texture.image.width / texture.image.height;
    const width = height * aspectRatio;
    const cardGeometry = new THREE.PlaneGeometry(width, height);

    //Not sure why rotation adjustment is needed???
    cardGeometry.rotateY(Math.PI);
    //////////////////////////////////////////////////

    // The gallery's artwork is photographic, and a texture that is not marked as sRGB is
    // read as linear — the work would hang on the planets looking washed out.
    texture.colorSpace = THREE.SRGBColorSpace;

    const cardMaterial = new THREE.MeshBasicMaterial({
        map: texture,
        side: THREE.DoubleSide,
    });
    const mesh = new THREE.Mesh(cardGeometry, cardMaterial);
    mesh.name = record.name;
    mesh.material.transparent = true;
    mesh.material.opacity = 0;
    mesh.userData.isImage = true;
    mesh.userData.group = imgGroup.parent.name;
    // The gallery's own record for this work — url, title, mediaType, id — carried onto the
    // mesh so that clicking or hovering it never has to go back to the API to find out what
    // it is.
    mesh.userData.art = record;
    // Which slot of the ring this work is. `resetImagePositions` places every card from its own
    // slot across the ring's whole count, so a work whose texture never arrives leaves the gap its
    // frame is drawn in rather than pulling every card after it off its own window.
    mesh.userData.slot = slot;
    mesh.layers.set(1);
    // Painted after the strip. The band carries no depth test — see filmStrip.js for why — so it is
    // drawn before the cards rather than after, and this number is what puts the artwork back on top
    // of its own frame.
    mesh.renderOrder = 1;
    imgGroup.add(mesh);

    // Born at 0, above, which is where a ring rests — then, if the camera is already on this
    // planet, born lit instead. The traverses in MainScene are the only other writers of a card's
    // opacity and they have already run, so a card that arrives after the click is a card nobody
    // ever fades in: it would sit in the ring invisible until the visitor left and came back.
    ringBirth(mesh);

    // Placed as it arrives, rather than waiting for the whole ring: a card has no `rad` and no
    // `angle` until this runs, and a planet can be selected long before the last texture lands —
    // the gallery holds work at 8192x2731 and 5225x8075, seconds of decoding — and a card without
    // them sits at its group's origin, which is inside the planet: an artwork nobody can see, and
    // the first pixel of a drag throws on `rad.minor`. Re-placing the ring per arrival costs
    // nothing worth counting and is invisible: a card is at opacity 0 until its planet is
    // selected — or at 1 straight away, if one already is.
    resetImagePositions(imgGroup);
}
